import { z } from 'zod';

// A named filter preset belongs to the ACCOUNT, not the browser. Both toolbars
// that offer one store it here; `scope` says which surface a preset belongs to
// so the two never bleed into each other's menus.
export const savedFilterScopeSchema = z.enum(['operations', 'itineraries']);
export type SavedFilterScope = z.infer<typeof savedFilterScopeSchema>;

// The preset's filter state, kept as an opaque record on the wire. Each surface
// owns its own shape and re-validates on read, which is what lets a preset
// saved against an older filter set survive a key the UI has since dropped —
// a stricter wire schema would reject the whole preset instead.
const payloadSchema = z.record(z.string(), z.unknown());

export const savedFilterSchema = z.object({
  id: z.uuid(),
  scope: savedFilterScopeSchema,
  name: z.string(),
  payload: payloadSchema,
  isDefault: z.boolean(),
  createdAt: z.string(),
});
export type SavedFilter = z.infer<typeof savedFilterSchema>;

export const listSavedFiltersInput = z.object({
  scope: savedFilterScopeSchema,
});

export const createSavedFilterInput = z.object({
  scope: savedFilterScopeSchema,
  name: z.string().trim().min(1).max(120),
  payload: payloadSchema,
  isDefault: z.boolean().default(false),
});

export const removeSavedFilterInput = z.object({ id: z.uuid() });

// Toggling rather than setting: the menus offer "make this the default", and
// clicking the current default clears it.
export const toggleSavedFilterDefaultInput = z.object({ id: z.uuid() });

// One-time adoption of whatever the browser still holds, so the move to
// per-account storage does not begin by deleting everyone's presets. Import is
// only ever applied to a scope the account has nothing in, so a second device
// running it later cannot duplicate or resurrect deleted presets.
export const importSavedFiltersInput = z.object({
  scope: savedFilterScopeSchema,
  presets: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(120),
        payload: payloadSchema,
        isDefault: z.boolean().default(false),
        createdAt: z.string().optional(),
      })
    )
    .max(100),
});
